import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';
import { StreakLayer } from '../particle-layers/streak-layer.js';
import { DustLayer } from '../particle-layers/dust-layer.js';

// Shared-rig contract (see the framing addendum): this visualizer's
// declared relationship to the one shared StageLightRig instance, and the
// beam intensity it asks for. Most restrictive wins across owners.
const RIG = 'required';
const RIG_OWNER = 'tunnel';
const RIG_INTENSITY_SCALE = 1;
const RIG_ZONES = 'default';
// Same lit-floor treatment as neon-pulse.js/kinetic-rig.js: relaxes the rig's
// spot falloff (decay 2.4 -> 1.1) so beams actually reach the ground and draw
// pools on it instead of arriving divided by ~170.
const RIG_FLOOR_LIGHT_BOOST = 3;
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'tunnel',
  family: 'particles',
  energyLevel: 0.70,
  bpmMin: 95,
  bpmMax: 175,
  movement: 0.60,
  tonality: 0.75,
  enabled: true
};

// Tunnel carries NeonPulse's whole context (its exact camera/rig/floor)
// with the streak tunnel layered on top, rather than StreakLayer
// standing alone under a first-person "flying down the tube" camera.
// That first-person framing is what forced an earlier rig-offset
// workaround (heads mounted high above a camera sitting right at the
// rig's own origin read as steep, cropped beams) - NeonPulse's own
// external "audience" camera is exactly the framing the rig was
// designed around, so switching to it removes the need for that offset
// entirely; the plain, unmodified StageLightRig below is deliberate,
// not an oversight.
//
// Only DustLayer, never StormLayer - "ne met jamais les particules de
// particulestorm, uniquement celles de neonpulse". NeonPulse itself
// cycles between the two at random; Tunnel deliberately doesn't (no
// cycling logic at all here - dust is the only option, permanently).
//
// Every piece here already reacts to the beat on its own (rig: see
// stage-light-rig.js's own BEAT_PULSE_*; dust: its own BEAT_PULSE_* on
// size/opacity) - streaks gained the same treatment too (see streak-
// layer.js's BEAT_PULSE_*), so nothing in the combined scene sits out
// the beat reactions.
const PARTICLES_CAMERA_POSITION = [0, 2.6, 7];
const PARTICLES_LOOK_AT = [0, 2, 0];

export class TunnelVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(PARTICLES_CAMERA_POSITION[0], PARTICLES_CAMERA_POSITION[1], PARTICLES_CAMERA_POSITION[2]);
    camera.lookAt(PARTICLES_LOOK_AT[0], PARTICLES_LOOK_AT[1], PARTICLES_LOOK_AT[2]);

    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES,
      floorLightBoost: RIG_FLOOR_LIGHT_BOOST
    });

    const floorGeometry = new THREE.PlaneGeometry(60, 60);
    const floorMaterial = new THREE.MeshStandardMaterial({ color: 0x16161c, roughness: 0.78, metalness: 0.0 });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    scene.add(this._floor);

    this._streaks = new StreakLayer();
    this._streaks.init(scene);

    this._dust = new DustLayer();
    this._dust.init(scene);
  }

  update(deltaTime, profile) {
    stageRig.update(deltaTime, profile, RIG_OWNER);
    this._streaks.update(deltaTime, profile);
    this._dust.update(deltaTime, profile);
  }

  getLightRig() {
    return this._rig;
  }

  dispose() {
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._streaks.dispose(this._scene);
    this._dust.dispose(this._scene);
  }
}
